import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { STRIPE_URL } from "@/lib/links";
import MaskedLines from "@/components/site/MaskedLines";
import Counter from "@/components/site/Counter";
import { track } from "@/lib/analytics";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const orbY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const orbY2 = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  useEffect(() => {
    // Prevent horizontal scroll during entry animations
    document.body.style.overflowX = "hidden";
  }, []);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full bg-[#06231D] text-[#F7F9F8] overflow-hidden grain"
    >
      {/* Gradient mesh orbs */}
      <motion.div
        style={{ y: orbY }}
        aria-hidden
        className="absolute -top-32 -right-32 w-[620px] h-[620px] rounded-full blur-[110px] opacity-70"
      >
        <div className="w-full h-full brand-gradient rounded-full" />
      </motion.div>
      <motion.div
        style={{ y: orbY2 }}
        aria-hidden
        className="absolute bottom-[-160px] left-[-120px] w-[520px] h-[520px] rounded-full blur-[130px] opacity-40"
      >
        <div className="w-full h-full rounded-full" style={{ background: "radial-gradient(circle,#C9A84C 0%, transparent 70%)" }} />
      </motion.div>

      {/* Dark green tactile texture overlay */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.12] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/4863013/pexels-photo-4863013.jpeg?auto=compress&cs=tinysrgb&w=1400')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          WebkitMaskImage: "linear-gradient(180deg, black 55%, transparent 100%)",
          maskImage: "linear-gradient(180deg, black 55%, transparent 100%)",
        }}
      />

      {/* Grid overlay */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(247,249,248,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(247,249,248,0.6) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
          WebkitMaskImage: "linear-gradient(180deg, black 55%, transparent 100%)",
          maskImage: "linear-gradient(180deg, black 55%, transparent 100%)",
        }}
      />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pt-28 pb-10 min-h-[100svh] flex flex-col justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.6 }}
          className="flex items-center justify-center gap-3 text-[11px] uppercase tracking-[0.28em] text-[#C9A84C] mb-10"
          data-testid="hero-kicker"
        >
          <span className="w-8 h-px bg-[#C9A84C]" />
          <span>
            Consultoría de talento <span className="hidden sm:inline">·&nbsp;</span>
            <br className="sm:hidden" />
            España &amp; LATAM
          </span>
        </motion.div>

        <motion.div style={{ y: textY }}>
          <MaskedLines
            as="h1"
            className="font-display font-semibold text-white text-center text-[13vw] sm:text-[9vw] lg:text-[6.5vw] 2xl:text-[92px] leading-[1.02] tracking-tight"
            lines={[
              <span key="l1">Deja de perder <span className="font-serif-italic text-[#D9BC6A] font-normal">TALENTO</span></span>,
              "por errores que ni",
              "siquiera sabes",
              <span key="l4">que estás <span className="font-serif-italic text-[#D9BC6A] font-normal">COMETIENDO.</span></span>,
            ]}
          />
        </motion.div>

        <div className="mt-12 lg:mt-16 max-w-2xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="text-white/70 text-lg lg:text-xl leading-relaxed font-light text-center"
            data-testid="hero-subtitle"
          >
            La mayoría de startups tech pierden talento valioso por errores que no saben que están cometiendo. No es mala suerte. Es falta de información.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6"
          >
            <a
              href={STRIPE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-button"
              onClick={() => track("cta_click", { location: "hero", product: "diagnostic" })}
              className="group inline-flex items-center gap-3 px-8 h-16 rounded-full brand-gradient text-white text-base font-medium cta-lift"
            >
              Descúbrelo aquí
              <span className="w-9 h-9 rounded-full bg-white/15 grid place-items-center group-hover:bg-white/25 transition-colors">
                <ArrowUpRight size={18} strokeWidth={1.5} />
              </span>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
